import { ErrorInfo } from "react";

import { parseMinifiedReactInvariant } from "~/helpers/reactMinifiedError";
import { loadReact17ProductionErrorCodes } from "~/helpers/react17ProductionErrorCodesLoader";
import { setNewRelicNoticeError } from "~/helpers/newrelic";

const MAX_COMPONENT_STACK_CHARS = 3500;
const MAX_DECODED_MESSAGE_CHARS = 500;
const MAX_ROUTE_CHARS = 500;

function truncate(value: string, maxChars: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxChars) return trimmed;
  return `${trimmed.slice(0, maxChars)}…`;
}

function buildBaseAttributes(
  error: Error,
  errorInfo: ErrorInfo,
  routePath: string | undefined,
  invariant: number | null
): Record<string, string> {
  const attrs: Record<string, string> = {
    errorBoundary: "true",
    originalErrorName: error?.name ?? "Error",
    componentStackTruncated: truncate(errorInfo?.componentStack ?? "", MAX_COMPONENT_STACK_CHARS),
    routePath: truncate(routePath ?? "", MAX_ROUTE_CHARS),
  };
  if (invariant != null) {
    attrs.reactInvariant = String(invariant);
  }
  return attrs;
}

async function reportErrorBoundaryToNewRelicAsync(
  error: Error,
  errorInfo: ErrorInfo,
  routePath?: string
): Promise<void> {
  const message = error?.message ?? "";
  const invariant = parseMinifiedReactInvariant(message);
  const customAttributes = buildBaseAttributes(error, errorInfo, routePath, invariant);

  try {
    if (invariant != null) {
      const codes = await loadReact17ProductionErrorCodes();
      const decodedRaw = codes[String(invariant)];
      if (typeof decodedRaw === "string") {
        customAttributes.reactInvariantDecoded = truncate(decodedRaw, MAX_DECODED_MESSAGE_CHARS);
      }
    }
  } catch {
    // Still report with reactInvariant / stack / route if JSON chunk fails to load
  }

  setNewRelicNoticeError(error, customAttributes);
}

/**
 * Reports an ErrorBoundary catch to New Relic with structured custom attributes
 * while preserving the original Error (message + stack) for existing queries.
 * JSON decode map is loaded asynchronously so it is not bundled on the critical path.
 */
export function reportErrorBoundaryToNewRelic(error: Error, errorInfo: ErrorInfo, routePath?: string): void {
  void reportErrorBoundaryToNewRelicAsync(error, errorInfo, routePath);
}
